import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Banknote, ClipboardList, PoundSterling, Receipt, RotateCcw, Volleyball, FileBarChart, KanbanSquare, MessageCircle, Bell, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LINKS = [
  { to: "/app/expenses", label: "Expense Reclaim", sub: "Approve & pay claims", Icon: Receipt, testid: "finance-link-expenses" },
  { to: "/app/fees", label: "Subscription Fees & Refunds", sub: "Fee grid, refund calculator & log", Icon: RotateCcw, testid: "finance-link-fees" },
  { to: "/app/fines", label: "League Fines", sub: "Open / paid, season totals, CSV", Icon: Receipt, testid: "finance-link-fines" },
  { to: "/app/referee-payments", label: "Referee Payments", sub: "Fees due by league & match", Icon: Volleyball, testid: "finance-link-referee-payments" },
  { to: "/app/reports", label: "Report Builder", sub: "Custom CSV reports", Icon: FileBarChart, testid: "finance-link-reports" },
  { to: "/app/tasks", label: "Task Board", sub: "Committee tasks", Icon: KanbanSquare, testid: "finance-link-tasks" },
  { to: "/app/chat", label: "Chat", sub: "Committee channels", Icon: MessageCircle, testid: "finance-link-chat" },
  { to: "/app/notices", label: "Notices", sub: "Club announcements", Icon: Bell, testid: "finance-link-notices" },
  { to: "/app/committee-contacts", label: "Committee Contacts", sub: "Who to call", Icon: Users, testid: "finance-link-contacts" },
];

export default function FinanceHome() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [range, setRange] = useState({ start: iso(first), end: iso(today) });
  const downloadPack = async () => {
    try {
      const { data } = await api.get("/finance/month-end-pack.csv", { params: range, responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `month-end-pack-${range.start}-to-${range.end}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Month-end pack downloaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Export failed"); }
  };
  useEffect(() => { api.get("/finance/summary").then(r => setS(r.data)).catch(() => setS({})); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="finance-home">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Treasurer workspace</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-1">Finance</h1>
        <p className="opacity-60 text-sm mt-2">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""} — everything money-related in one place.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="finance-kpis">
        <Tile to="/app/expenses" testid="kpi-claims-to-pay" Icon={Banknote} label="Claims to pay"
          value={s ? `${s.claims_to_pay?.count ?? 0}` : "…"} sub={s ? `${gbp(s.claims_to_pay?.total)} approved · ${s.claims_to_approve?.count ?? 0} awaiting approval` : ""} accent />
        <Tile to="/app/fees" testid="kpi-refunds" Icon={RotateCcw} label="Refund requests"
          value={s ? `${(s.refunds_requested ?? 0) + (s.refunds_approved ?? 0)}` : "…"} sub={s ? `${s.refunds_requested ?? 0} to approve · ${s.refunds_approved ?? 0} to pay` : ""} />
        <Tile to="/app/fees" testid="kpi-fees" Icon={PoundSterling} label={s?.fee_grid ? `Fees ${s.fee_grid.season}` : "Subscription fees"}
          value={s?.fee_grid ? (s.fee_grid.categories || []).map(c => `${c.name?.split(" ")[0]} ${gbp(c.season_fee).replace(".00", "")}`).slice(0, 2).join(" · ") : s ? "No grid" : "…"}
          sub={s ? `${gbp(s.refunds_paid_total)} refunded this season` : ""} small />
        <Tile to="/app/referee-payments" testid="kpi-ref-fees" Icon={Volleyball} label={`Referee fees · ${s?.referee_fees_month?.month || "this month"}`}
          value={s ? gbp(s.referee_fees_month?.total) : "…"} sub={s ? `${s.referee_fees_month?.count ?? 0} confirmed league/cup games${s.fines_open?.count ? ` · ${s.fines_open.count} league fine${s.fines_open.count === 1 ? "" : "s"} open (${gbp(s.fines_open.total)})` : ""}` : ""} />
      </div>
      <div className="mt-10 card-tactical rounded-md p-5" data-testid="month-end-pack">
        <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Month-end pack</div>
        <div className="text-sm opacity-70 mt-1">One CSV bundle for any period: paid expenses, paid refunds and referee fees, each with totals.</div>
        <div className="flex flex-wrap items-end gap-3 mt-3">
          <label className="text-xs">From<input type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} data-testid="pack-start" className="block mt-1 bg-background border border-border rounded px-2 py-1.5" /></label>
          <label className="text-xs">To<input type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} data-testid="pack-end" className="block mt-1 bg-background border border-border rounded px-2 py-1.5" /></label>
          <button type="button" onClick={downloadPack} className="btn-volt px-5 py-2 rounded-md text-xs tracking-widest uppercase" data-testid="pack-download">Download pack</button>
        </div>
      </div>
      <div className="mt-10">
        <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-3">Finance pages</div>
        <div className="grid sm:grid-cols-2 gap-2" data-testid="finance-links">
          {LINKS.map(({ to, label, sub, Icon, testid }) => (
            <Link key={to} to={to} data-testid={testid} className="card-tactical rounded-md p-4 flex items-center gap-4 hover:border-[#83B68B] transition-colors group">
              <Icon className="w-5 h-5 text-[#83B68B] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold tracking-tight">{label}</div>
                <div className="text-xs opacity-60">{sub}</div>
              </div>
              <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Tile({ to, testid, Icon, label, value, sub, accent, small }) {
  return (
    <Link to={to} data-testid={testid} className={`card-tactical rounded-md p-5 block hover:border-[#83B68B] transition-colors ${accent ? "border-[#83B68B]/50" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-widest uppercase opacity-60">{label}</div>
        <Icon className="w-4 h-4 text-[#83B68B]" />
      </div>
      <div className={`font-display font-black mt-3 ${small ? "text-lg" : "text-4xl"} tracking-tight`}>{value}</div>
      <div className="text-xs opacity-60 mt-1 min-h-[1rem]">{sub}</div>
    </Link>
  );
}
